/**
 * 公共参数拦截器
 *
 * 为所有 API 请求自动添加公共参数（与 v2 保持一致）：
 * 1. Authorization 请求头（如果已登录）
 * 2. 所有公共参数：curr_ver, soft_id, mac_code, device_code, os, source, device_id, ekey
 * 3. 可选推广参数：mp, bdVid, qhclickid, strategy, invite_id
 *
 * 注意：
 * - 如果 URL 中已经包含某个参数，则不会重复添加
 * - 拦截器失败不会阻止请求，会记录错误日志并返回原始请求
 */

import type { HttpRequest } from '../../../types/api'
import type { IStorage } from '../../storage/Storage'
import type { IDeviceInfoManager } from '../../services/DeviceInfoManager'
import { hasUrlParam, stringifyQueryParams } from '../../utils/url'

/**
 * 公共参数拦截器配置
 */
export interface ParamsInterceptorConfig {
  /** 是否启用推广参数（默认 true） */
  enablePromotionParams?: boolean
  /** 需要排除的 URL 模式（正则表达式） */
  excludePatterns?: RegExp[]
  /** 当前认证 token 来源，优先于 storage，避免刷新后继续读取旧缓存 */
  getToken?: () => string | null
}

/**
 * 检查 URL 是否应该被排除
 */
function shouldExclude(url: string, patterns?: RegExp[]): boolean {
  if (!patterns || patterns.length === 0) {
    return false
  }
  return patterns.some(pattern => pattern.test(url))
}

/**
 * 检查 URL 中是否已经包含某个参数
 */
/**
 * 从 storage 中读取 token
 */
function getTokenFromStorage(storage: IStorage): string | null {
  // 尝试多种可能的 key（兼容不同项目的存储格式）
  const possibleKeys = ['token', 'csd_token']

  for (const key of possibleKeys) {
    try {
      const data = storage.getItem<string>(key)
      if (data) {
        // 尝试解析 JSON 格式
        try {
          const parsed = JSON.parse(data)
          if (parsed.token) return parsed.token
          if (typeof parsed === 'string') return parsed
        } catch {
          // 如果不是 JSON，直接返回
          return data
        }
      }
    } catch {
      // 继续尝试下一个 key
    }
  }

  // 尝试直接从 localStorage 读取（兼容旧版）
  if (typeof localStorage !== 'undefined') {
    try {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          const parsed = JSON.parse(token)
          if (parsed.token) return parsed.token
          if (parsed.value?.token) return parsed.value.token
          if (typeof parsed.value === 'string') return parsed.value
        } catch {
          return token
        }
      }
    } catch {
      // ignore
    }
  }

  return null
}

/**
 * 获取当前请求 token。
 * 登录刷新会先更新 AuthStore，storage 里可能短时间仍残留旧 token，请求头必须以当前登录态为准。
 */
function getRequestToken(storage: IStorage, config?: ParamsInterceptorConfig): string | null {
  if (config?.getToken) {
    return config.getToken() || null
  }
  return getTokenFromStorage(storage)
}

/**
 * 创建公共参数拦截器
 *
 * @param deviceInfoManager 设备信息管理器
 * @param storage 存储实例
 * @param config 拦截器配置
 * @returns 拦截器函数
 */
export function createParamsInterceptor(
  deviceInfoManager: IDeviceInfoManager,
  storage: IStorage,
  config?: ParamsInterceptorConfig
): (request: HttpRequest) => Promise<HttpRequest> {
  const enablePromotionParams = config?.enablePromotionParams !== false
  const excludePatterns = config?.excludePatterns || []

  return async (request: HttpRequest): Promise<HttpRequest> => {
    try {
      // 检查是否应该排除此 URL
      if (shouldExclude(request.url, excludePatterns)) {
        return request
      }

      // 1. 添加 Authorization 请求头（如果已登录）
      const token = getRequestToken(storage, config)
      if (token) {
        // 注意：API 服务器要求 Authorization 直接使用 token，不带 "Bearer " 前缀
        request.headers = {
          ...request.headers,
          Authorization: token
        }
      }

      // 2. 获取所有公共参数
      const commonParams = deviceInfoManager.getCommonParams()

      // 3. 构建需要添加的参数对象
      const paramsToAdd: Record<string, string | number> = {}

      // 添加公共参数（只添加 URL 中不存在的）
      if (!hasUrlParam(request.url, 'curr_ver')) {
        paramsToAdd.curr_ver = commonParams.curr_ver
      }
      if (!hasUrlParam(request.url, 'soft_id')) {
        paramsToAdd.soft_id = commonParams.soft_id
      }
      if (!hasUrlParam(request.url, 'mac_code')) {
        paramsToAdd.mac_code = commonParams.mac_code
      }
      if (!hasUrlParam(request.url, 'device_code')) {
        paramsToAdd.device_code = commonParams.device_code
      }
      if (!hasUrlParam(request.url, 'os')) {
        paramsToAdd.os = commonParams.os
      }
      if (!hasUrlParam(request.url, 'source')) {
        paramsToAdd.source = commonParams.source
      }
      if (!hasUrlParam(request.url, 'device_id')) {
        paramsToAdd.device_id = commonParams.device_id
      }
      if (!hasUrlParam(request.url, 'ekey')) {
        paramsToAdd.ekey = commonParams.ekey
      }

      // 4. 添加推广参数（如果启用）
      if (enablePromotionParams) {
        const promotionParams = deviceInfoManager.getPromotionParams()

        if (promotionParams.mp && !hasUrlParam(request.url, 'mp')) {
          paramsToAdd.mp = promotionParams.mp
        }
        if (promotionParams.bdVid && !hasUrlParam(request.url, 'bdVid')) {
          paramsToAdd.bdVid = promotionParams.bdVid
        }
        if (promotionParams.qhclickid && !hasUrlParam(request.url, 'qhclickid')) {
          paramsToAdd.qhclickid = promotionParams.qhclickid
        }
        if (promotionParams.strategy && !hasUrlParam(request.url, 'strategy')) {
          paramsToAdd.strategy = promotionParams.strategy
        }
        if (promotionParams.invite_id && !hasUrlParam(request.url, 'invite_id')) {
          paramsToAdd.invite_id = promotionParams.invite_id
        }
      }

      // 5. 如果有需要添加的参数，构建查询字符串并添加到 URL
      if (Object.keys(paramsToAdd).length > 0) {
        const queryString = stringifyQueryParams(paramsToAdd)

        if (queryString) {
          const separator = request.url.includes('?') ? '&' : '?'
          request.url = `${request.url}${separator}${queryString}`
        }
      }

      return request
    } catch (error) {
      // 拦截器失败不应阻止请求
      console.error('[ParamsInterceptor] Failed to process request:', error)
      return request
    }
  }
}
